import { newId } from './idGen';

/**
 * Shared bracket math used by single_elimination, group_knockout (knockout stage),
 * and compass_draw (main bracket + every feeder direction). Kept dependency-free
 * and framework-agnostic — pure functions over plain data.
 */

export const nextPowerOf2 = (n) => {
  let p = 1;
  while (p < n) p *= 2;
  return p;
};

/**
 * Standard tournament bracket seeding order, e.g. size=8 -> [1,8,4,5,2,7,3,6]
 * so seed 1 and seed 2 can only meet in the final.
 */
export const buildSeedOrder = (size) => {
  let order = [1];
  let n = 1;
  while (n < size) {
    const next = [];
    for (const s of order) {
      next.push(s);
      next.push(2 * n + 1 - s);
    }
    order = next;
    n *= 2;
  }
  return order;
};

export const roundLabel = (round, totalRounds) => {
  const remaining = totalRounds - round;
  if (remaining === 0) return 'Final';
  if (remaining === 1) return 'Semifinal';
  if (remaining === 2) return 'Quarterfinal';
  return `Round of ${2 ** (remaining + 1)}`;
};

/**
 * Builds a full single-elimination bracket from a flat list of slot entries.
 * Slot entries can be real participants ({ id, name }) or placeholders
 * ({ id, name, isPlaceholder: true }) representing an as-yet-unknown qualifier
 * (e.g. "Group A · 1st place", "Loser of East R1 #2") — the shape is identical
 * either way, only isPlaceholder differs, so downstream rendering doesn't branch.
 */
export const buildBracket = (slotEntries, label = '') => {
  const n = slotEntries.length;
  const size = nextPowerOf2(n);
  const order = buildSeedOrder(size);
  const slots = order.map((seed) => (seed <= n ? slotEntries[seed - 1] : null));
  const totalRounds = Math.log2(size);
  const rounds = [];

  // `number` is sequential across the WHOLE bracket (round 1 first, then round 2, ...),
  // not reset per round — so "Winner of Match 7" always means one specific match,
  // never collides with a same-numbered match in a different round.
  let matchCounter = 1;
  let prevRoundMatches = null;

  for (let r = 1; r <= totalRounds; r++) {
    const matchCount = size / 2 ** r;
    const matches = [];
    for (let m = 0; m < matchCount; m++) {
      const slotA = r === 1 ? slots[m * 2] : null;
      const slotB = r === 1 ? slots[m * 2 + 1] : null;
      const feederA = r > 1 ? prevRoundMatches[m * 2] : null;
      const feederB = r > 1 ? prevRoundMatches[m * 2 + 1] : null;
      matches.push({
        id: newId(),
        number: matchCounter++,
        round: r,
        slotA,
        slotB,
        // Short form ("M1 Winner") on purpose — "Winner of Match 1" was
        // wide enough to truncate ("Winner of Matc...") in compact cards.
        placeholderA: feederA ? `M${feederA.number} Winner` : null,
        placeholderB: feederB ? `M${feederB.number} Winner` : null,
        isBye: r === 1 && Boolean(slotA || slotB) && !(slotA && slotB),
      });
    }
    rounds.push({ id: newId(), round: r, label: roundLabel(r, totalRounds), matches });
    prevRoundMatches = matches;
  }

  return { label, participantsCount: n, bracketSize: size, byes: size - n, totalRounds, rounds };
};

export const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
